<script setup>
import ModalWrapper from '@/modals/ModalWrapper';
import { ref } from 'vue';
import { useToast } from 'vue-toastification';
import { useProgramStore } from '@/stores/program';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';

const props = defineProps({
    program: {
        type: Object,
        default: () => ({
            lender_id: null,
            program_name: '',
            vehicle_min_year: null,
            vehicle_max_year: null,
            effective_date: null,
            end_date: null,
            is_active: true,
        })
    },
    mode: {
        type: String,
        default: 'Create'
    }
});

const emit = defineEmits(['close-modal']);
const form = ref(
    {
        ...props.program,
        effective_date: props.program.effective_date ? new Date(props.program.effective_date) : null,
        end_date: props.program.end_date ? new Date(props.program.end_date) : null,
    }
);

const toast = useToast();

const programStore = useProgramStore();
const { store, update, copy } = programStore;
const { lenderOptions, yearOptions } = storeToRefs(useOptionsStore());

const save = async () => {
    try {
        let action;
        if (props.mode === 'Create') {
            action = store;
        } else  if (props.mode === 'Update') {
            action = update;
        } else {
            action = copy;
        }

        let toastMessage = props.mode === 'Create' ? 'Program Created' : props.mode === 'Update' ? 'Program Updated' : 'Program Copied';

        await action({
            ...form.value,
        });

        toast.success(toastMessage);
        emit('close-modal');
    } catch (error) {
        toast.error('An unexpected error occurred');
    }
};
</script>

<template>
<ModalWrapper>
    <template #header>{{ mode }} {{ $t('label.program') }}</template>
    <template #subheader>{{ $t('label.create_or_edit') }}</template>

    <form @submit.prevent="save">
        <div class="grid md:grid-cols-2 gap-x-4 gap-y-3">
            <StyledDropdown
                v-model="form.lender_id"
                :options="lenderOptions"
                name="lender_id"
                :placeholder="$t('label.lender')"
                :label="$t('label.lender')" />
            <StyledInput
                v-model="form.program_name"
                name="program_name"
                :placeholder="$t('label.program_name')"
                :label="$t('label.program_name')" />
            <StyledDropdown
                v-model="form.vehicle_min_year"
                :options="yearOptions"
                name="vehicle_min_year"
                :placeholder="$t('label.vehicle_min_year')"
                :label="$t('label.vehicle_min_year')"
                type="number" />
            <StyledDropdown
                v-model="form.vehicle_max_year"
                :options="yearOptions"
                name="vehicle_max_year"
                :placeholder="$t('label.vehicle_max_year')"
                :label="$t('label.vehicle_max_year')"
                type="number" />
            <StyledDatepicker
                v-model="form.effective_date"
                :required="false"
                :clearable="true"
                name="effective_date"
                :label="$t('label.effective_date')"
                :placeholder="$t('label.effective_date')" />
            <StyledDatepicker
                v-model="form.end_date"
                name="end_date"
                :label="$t('label.end_date')"
                :placeholder="$t('label.end_date')" />
            <StyledRoundSwitch
                v-model="form.is_active"
                :label="$t('label.active')"
                inset
                name="is_active" />
        </div>
    </form>

    <template #footer>
        <StyledSaveButton modal @save="save" />
    </template>
</ModalWrapper>
</template>
